import configuration from "@/configuration";
import { useLocaleStore } from "@/stores";
import { useLayoutEffect, useState } from "react";
import Flag from "react-flagpack";
import { Link, useParams } from "react-router-dom";
import { LanguageDropdown, LanguageDropdownLabel, LanguageDropdownOption } from "./components/LanguageDropdown";
import { LocaleDropdown, LocaleDropdownLabel, LocaleDropdownOption } from "./components/LocaleDropdown";
import Search from "./components/Search";

type CountryOption = {
    id: string;
    name: string;
};

type LanguageOption = {
    id: string;
    name: string;
};

const TopBar = () => {
    const activeRegion = useLocaleStore((state) => state.activeRegion);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);
    const availableRegions = useLocaleStore((state) => state.availableRegions);
    const availableLanguages = useLocaleStore((state) => state.availableLanguages);
    const setActiveRegion = useLocaleStore((state) => state.setActiveRegion);
    const setActiveLanguage = useLocaleStore((state) => state.setActiveLanguage);

    const [countries, setCountries] = useState<CountryOption[]>([]);
    const [languages, setLanguages] = useState<LanguageOption[]>([]);

    const { locale } = useParams();

    useLayoutEffect(() => {
        const countries: CountryOption[] = [];

        availableRegions.forEach((region) => {
            let label = "";
            if (Object.keys(region.labels).includes(activeLanguage!)) {
                label = region.labels[activeLanguage!];
            } else if (Object.keys(region.labels).includes("en")) {
                label = region.labels["en"];
            } else {
                label = Object.values(region.labels)[0];
            }

            countries.push({ id: region.slug, name: label });
        });

        setCountries(countries);
    }, [availableRegions, activeLanguage]);

    useLayoutEffect(() => {
        const languages: LanguageOption[] = [];

        availableLanguages[activeRegion!].forEach((language) => {
            const languageSlug = language.slug.split("-")[1].toLowerCase();
            languages.push({ id: languageSlug, name: language.label });
        });

        setLanguages(languages);
    }, [availableLanguages, activeRegion]);

    const onChangeCountry = (value: CountryOption) => {
        const newLanguage =
            availableLanguages[value.id].find(
                (language) => language.active && language.slug.endsWith(`-${activeLanguage}`)
            ) || availableLanguages[value.id][0];

        setActiveRegion(value.id);
        setActiveLanguage(newLanguage.slug.split("-")[1].toLowerCase());
    };

    return (
        <div id="top-bar" className="container mx-auto 2xl:px-[108px] flex center h-[76px] items-center py-2 gap-x-8">
            <Link to={`/${locale}`} className="shrink-0">
                <img
                    src={`${configuration.assets}content/logo-bimids.png`}
                    alt="BIMids logo"
                    className="h-[52px] w-auto mt-2"
                />
            </Link>

            <div className="grow">
                <Search />
            </div>

            <div>
                <LocaleDropdown
                    value={countries.find((x) => x.id === activeRegion) ?? { id: "", name: "" }}
                    onChange={onChangeCountry}
                >
                    {countries.map((value) => (
                        <LocaleDropdownOption key={value.id} value={value}>
                            <Flag code={value.id.toUpperCase()} size="m" hasBorder={false} />
                            <LocaleDropdownLabel>{value.name}</LocaleDropdownLabel>
                        </LocaleDropdownOption>
                    ))}
                </LocaleDropdown>
            </div>

            <div>
                <LanguageDropdown
                    value={languages.find((x) => x.id === activeLanguage) ?? { id: "", name: "" }}
                    onChange={(value) => setActiveLanguage(value.id)}
                >
                    {languages.map((value) => (
                        <LanguageDropdownOption key={value.id} value={value}>
                            <LanguageDropdownLabel>{value.name}</LanguageDropdownLabel>
                        </LanguageDropdownOption>
                    ))}
                </LanguageDropdown>
            </div>
        </div>
    );
};

export default TopBar;
